import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { ArrowLeft, Download } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useSurvey,
  useSurveyResults,
  useUpdateSurvey,
  useDeleteSurvey,
  usePublishSurvey,
} from '@/hooks/useSurveys'
import SurveyForm from '@/components/features/surveys/SurveyForm'
import SurveyResults from '@/components/features/surveys/SurveyResults'
import PublishDialog from '@/components/features/surveys/PublishDialog'
import { downloadSurveyReport } from '@/utils/surveyReport'
import type { CreateSurveyInput, Survey } from '@/services/api/surveys'

function getEffectiveStatus(survey: Survey): string {
  if (survey.status === 'draft') return 'draft'
  if (!survey.opens_at || !survey.closes_at) return 'draft'
  const now = new Date()
  const opens = new Date(survey.opens_at)
  const closes = new Date(survey.closes_at)
  if (now < opens) return 'scheduled'
  if (now >= closes) return 'closed'
  return 'open'
}

const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  open: 'default',
  closed: 'outline',
}

export function SurveyDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)

  const { data: survey, isLoading, error } = useSurvey(id!)
  const { data: results } = useSurveyResults(id!)

  const updateMutation = useUpdateSurvey()
  const deleteMutation = useDeleteSurvey()
  const publishMutation = usePublishSurvey()

  const handleUpdate = async (input: CreateSurveyInput) => {
    try {
      await updateMutation.mutateAsync({
        id: id!,
        input: {
          title: input.title,
          description: input.description,
          likertQuestions: input.likertQuestions,
          freeTextQuestion: input.freeTextQuestion,
        },
      })
      toast({ title: 'Survey updated' })
      setEditOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id!)
      toast({ title: 'Survey deleted' })
      navigate('/surveys')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  const handlePublish = async (opensAt: string, closesAt: string) => {
    try {
      await publishMutation.mutateAsync({ id: id!, input: { opensAt, closesAt } })
      toast({ title: 'Survey published' })
      setPublishOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Publish failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  const handleDownloadReport = () => {
    if (!survey || !results) return
    try {
      downloadSurveyReport(survey, results)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (error || !survey) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/surveys')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Surveys
        </Button>
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Survey not found'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const effectiveStatus = getEffectiveStatus(survey)
  const isDraft = effectiveStatus === 'draft'

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/surveys')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Badge variant={statusVariants[effectiveStatus] ?? 'outline'} className="text-sm">
          {effectiveStatus}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle className="text-xl">{survey.title}</CardTitle>
            <div className="flex gap-2 flex-shrink-0">
              {results && results.total_responses > 0 && (
                <Button variant="outline" size="sm" onClick={handleDownloadReport}>
                  <Download className="mr-2 h-4 w-4" />
                  Download Report
                </Button>
              )}
              {isDraft && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                  <Button size="sm" onClick={() => setPublishOpen(true)}>
                    Publish
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {survey.description && <p className="text-muted-foreground">{survey.description}</p>}

          <div className="space-y-2">
            <h3 className="text-sm font-medium">Likert Questions</h3>
            {survey.likert_questions.map((q, i) => (
              <div key={q.id} className="flex items-center gap-3 rounded-md border p-3">
                <span className="text-sm font-medium text-muted-foreground">{i + 1}.</span>
                <span>{q.text}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium">Free-Text Question</h3>
            <div className="rounded-md border p-3">{survey.free_text_question}</div>
          </div>

          {survey.opens_at && survey.closes_at && (
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Opens: {new Date(survey.opens_at).toLocaleString()}</p>
              <p>Closes: {new Date(survey.closes_at).toLocaleString()}</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Created {new Date(survey.created_at).toLocaleString()}
          </p>
        </CardContent>
      </Card>

      {results && results.total_responses > 0 && (
        <SurveyResults results={results} questionIds={survey.likert_questions.map((q) => q.id)} />
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Survey</DialogTitle>
            <DialogDescription>Update the survey questions and details.</DialogDescription>
          </DialogHeader>
          <SurveyForm
            initialTitle={survey.title}
            initialDescription={survey.description ?? ''}
            initialQuestions={survey.likert_questions}
            initialFreeTextQuestion={survey.free_text_question}
            onSubmit={handleUpdate}
            isPending={updateMutation.isPending}
            submitLabel="Save Changes"
          />
        </DialogContent>
      </Dialog>

      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublish={handlePublish}
        isPending={publishMutation.isPending}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this survey?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The survey and all its responses will be permanently
              deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
